// File: src/components/landing/Footer.tsx
import React from 'react';
import { 
  Box, 
  Container, 
  Grid, 
  Typography, 
  Link, 
  IconButton,
  Stack,
  useTheme,
} from '@mui/material';
import {
  Facebook,
  /*Twitter,*/
  Instagram,
  LinkedIn,
  Email,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const Footer: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();

  const handleNavigation = (path: string) => {
    navigate(path);
    window.scrollTo(0, 0);
  };

  const platformLinks = [
    { title: 'How It Works', path: '/how-it-works/' },
    { title: 'Request a Demo', path: '/contact?reason=requesting_demo' }
  ];

  const companyLinks = [
    { title: 'About Us', path: '/about' },
    { title: 'Contact Us', path: '/contact' }
  ];

  const socialLinks = [
    { icon: Facebook, url: 'https://facebook.com/dreamzconnectorg' },
    /*{ icon: Twitter, url: 'https://twitter.com/dreamzconnectorg' },*/
    { icon: Instagram, url: 'https://instagram.com/dreamzconnectorg' },
    { icon: LinkedIn, url: 'https://www.linkedin.com/company/dreamzconnect' },
  ];

  const contactInfo = [
    { 
      icon: Email, 
      text: 'info@dreamzconnect.org',
      url: 'mailto:info@dreamzconnect.org'
    }
  ];

  return (
    <Box
      component="footer"
      sx={{
        position: 'relative',
        mt: 'auto',
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
        color: 'white',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '2px',
          background: 'linear-gradient(to right, transparent, rgba(255,255,255,0.3), transparent)',
        },
      }}
    >
      {/* Main Footer Content */}
      <Container 
        maxWidth="xl"
        sx={{ 
          position: 'relative',
          zIndex: 1,
          pt: { xs: 6, md: 8 },
          pb: { xs: 4, md: 5 },
        }}
      >
        <Grid 
          container 
          spacing={4}
          alignItems="flex-start"
          sx={{ mb: { xs: 4, md: 6 } }}
        >
          {/* Brand Section */}
          <Grid item xs={12} md={4}>
            <Box sx={{ pr: { md: 4 } }}>
              <Typography 
                variant="h5"
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  background: 'linear-gradient(45deg, #FFFFFF 30%, rgba(255,255,255,0.8) 90%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Dreamz Connect
              </Typography>
              <Typography 
                sx={{ 
                  mb: 3,
                  opacity: 0.9,
                  lineHeight: 1.6,
                  fontSize: '1rem',
                }}
              >
                Transforming community service into meaningful career opportunities for students.
              </Typography>
              <Stack 
                direction="row" 
                spacing={2}
                sx={{ mb: { xs: 3, md: 0 } }}
              >
                {socialLinks.map(({ icon: Icon, url }) => (
                  <IconButton
                    key={url}
                    component="a"
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      color: 'white',
                      backgroundColor: 'rgba(255,255,255,0.1)',
                      backdropFilter: 'blur(10px)',
                      '&:hover': {
                        backgroundColor: 'rgba(255,255,255,0.2)',
                        transform: 'translateY(-2px)',
                      },
                      transition: 'all 0.2s ease-in-out',
                    }}
                  >
                    <Icon />
                  </IconButton>
                ))}
              </Stack>
            </Box>
          </Grid>

          {/* Links Section */}
          <Grid item container xs={12} md={8} spacing={4}>
            <Grid item xs={6} sm={4}>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 600,
                  mb: 3,
                  fontSize: '1.1rem',
                }}
              >
                Company
              </Typography>
              <Stack spacing={2}>
                {companyLinks.map(({ title, path }) => (
                  <Link
                    key={path}
                    component="button"
                    onClick={() => handleNavigation(path)}
                    sx={{
                      color: 'white',
                      opacity: 0.8,
                      textAlign: 'left',
                      textTransform: 'none',
                      fontSize: '1rem',
                      '&:hover': {
                        opacity: 1,
                        transform: 'translateX(5px)',
                      },
                      transition: 'all 0.2s ease-in-out',
                    }}
                  >
                    {title}
                  </Link>
                ))}
              </Stack>
            </Grid>

            <Grid item xs={6} sm={4}>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 600,
                  mb: 3,
                  fontSize: '1.1rem',
                }}
              >
                Platform
              </Typography>
              <Stack spacing={2}>
                {platformLinks.map(({ title, path }) => (
                  <Link
                    key={path}
                    component="button"
                    onClick={() => handleNavigation(path)}
                    sx={{
                      color: 'white',
                      opacity: 0.8,
                      textAlign: 'left',
                      textTransform: 'none',
                      fontSize: '1rem',
                      '&:hover': {
                        opacity: 1,
                        transform: 'translateX(5px)',
                      },
                      transition: 'all 0.2s ease-in-out',
                    }}
                  >
                    {title}
                  </Link>
                ))}
              </Stack>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 600,
                  mb: 3,
                  fontSize: '1.1rem',
                }}
              >
                Email
              </Typography>
              <Stack spacing={2}>
                {contactInfo.map(({ icon: Icon, text, url }) => (
                  <Link
                    key={text}
                    href={url}
                    sx={{
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      opacity: 0.8,
                      textDecoration: 'none',
                      fontSize: '1rem',
                      '&:hover': {
                        opacity: 1,
                      },
                      transition: 'opacity 0.2s ease-in-out',
                    }}
                  >
                    <Icon sx={{ fontSize: 20 }} />
                    <span>{text}</span>
                  </Link>
                ))}
              </Stack>
            </Grid>
          </Grid>
        </Grid>

        {/* Copyright Section */}
        <Box
          sx={{
            pt: 3,
            mt: 3,
            borderTop: '1px solid rgba(255,255,255,0.1)',
            textAlign: 'center',
          }}
        >
          <Typography 
            sx={{ 
              opacity: 0.8,
              fontSize: '0.9rem',
            }}
          >
            © {new Date().getFullYear()} DreamzConnect. All rights reserved.
          </Typography>
        </Box>
      </Container>

      {/* Background Effects */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at top right, rgba(255,255,255,0.1) 0%, transparent 60%)',
          pointerEvents: 'none',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at bottom left, rgba(0,0,0,0.2) 0%, transparent 60%)',
          pointerEvents: 'none',
        }}
      />
    </Box>
  );
};

export default Footer;